import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { ArrowLeft, ArrowRight, Clock, User } from 'lucide-react';
import { getInsightBySlug, getAllInsights, insightCategories } from '../data/insights';
import { useLanguage } from '../context/LanguageContext';

const InsightArticle = () => {
  const { t, language } = useLanguage();
  const { slug } = useParams();
  const insight = getInsightBySlug(slug);
  const allInsights = getAllInsights();
  
  // Get related articles (same category, excluding current)
  const relatedInsights = insight 
    ? allInsights.filter(i => i.category === insight.category && i.id !== insight.id).slice(0, 2)
    : [];

  // Update SEO metadata
  useEffect(() => {
    if (insight) {
      const baseUrl = 'https://mundalo.com';
      const articleUrl = `${baseUrl}/insights/${insight.slug}`;
      
      document.title = insight.seo?.title || `${insight.title} | Mundalo`;
      
      // Helper to set or create meta tag
      const setMetaTag = (selector, attribute, value) => {
        let tag = document.querySelector(selector);
        if (!tag) {
          tag = document.createElement('meta');
          if (selector.includes('property=')) {
            tag.setAttribute('property', selector.match(/property="([^"]+)"/)[1]);
          } else if (selector.includes('name=')) {
            tag.setAttribute('name', selector.match(/name="([^"]+)"/)[1]);
          }
          document.head.appendChild(tag);
        }
        tag.setAttribute(attribute, value);
      };

      // Helper to set or create link tag
      const setLinkTag = (rel, href) => {
        let tag = document.querySelector(`link[rel="${rel}"]`);
        if (!tag) {
          tag = document.createElement('link');
          tag.setAttribute('rel', rel);
          document.head.appendChild(tag);
        }
        tag.setAttribute('href', href);
      };

      // Meta description
      setMetaTag('meta[name="description"]', 'content', insight.seo?.description || insight.excerpt);

      // Canonical URL
      setLinkTag('canonical', articleUrl);

      // Open Graph tags
      setMetaTag('meta[property="og:title"]', 'content', insight.seo?.title || insight.title);
      setMetaTag('meta[property="og:description"]', 'content', insight.seo?.description || insight.excerpt);
      setMetaTag('meta[property="og:image"]', 'content', insight.heroImage);
      setMetaTag('meta[property="og:url"]', 'content', articleUrl);
      setMetaTag('meta[property="og:type"]', 'content', 'article');
      setMetaTag('meta[property="og:site_name"]', 'content', 'Mundalo');
      setMetaTag('meta[property="article:published_time"]', 'content', insight.publishedAt);
      setMetaTag('meta[property="article:author"]', 'content', insight.author);

      // Twitter Card tags
      setMetaTag('meta[name="twitter:card"]', 'content', 'summary_large_image');
      setMetaTag('meta[name="twitter:title"]', 'content', insight.seo?.title || insight.title);
      setMetaTag('meta[name="twitter:description"]', 'content', insight.seo?.description || insight.excerpt);
      setMetaTag('meta[name="twitter:image"]', 'content', insight.heroImage);
    }

    return () => {
      // Reset title on unmount
      document.title = 'Mundalo';
    };
  }, [insight]);

  // Category translations
  const categoryTranslations = {
    'Market Analysis': language === 'es' ? 'Análisis de Mercado' : 'Market Analysis',
    'Relocation': language === 'es' ? 'Reubicación' : 'Relocation',
    'Legal & Tax': language === 'es' ? 'Legal e Impuestos' : 'Legal & Tax',
    'Investment': language === 'es' ? 'Inversión' : 'Investment'
  };

  if (!insight) {
    return (
      <div className="min-h-screen bg-[#F9F6EF]">
        <Header />
        <div className="pt-32 pb-20 text-center">
          <h1 className="text-3xl font-light text-white mb-4">
            {language === 'es' ? 'Artículo No Encontrado' : 'Article Not Found'}
          </h1>
          <p className="text-[#888] mb-8">
            {language === 'es' ? 'El artículo que buscas no existe.' : "The article you're looking for doesn't exist."}
          </p>
          <Link to="/insights" className="text-[#2CA8A0] hover:underline">
            ← {t('insights.backToInsights')}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const category = insightCategories.find(c => c.id === insight.category);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric', timeZone: 'America/Chicago'});
  };

  const renderContentBlock = (block, index) => {
    switch (block.type) {
      case 'paragraph':
        return (
          <p key={index} className="text-[#1A5C6E] leading-relaxed mb-6">
            {block.text}
          </p>
        );
      
      case 'heading':
        const HeadingTag = `h${block.level}`;
        return (
          <HeadingTag 
            key={index} 
            className={`font-light text-white mt-10 mb-4 ${
              block.level === 2 ? 'text-2xl' : 'text-xl'
            }`}
          >
            {block.text}
          </HeadingTag>
        );
      
      case 'image':
        return (
          <figure key={index} className="my-10">
            <img 
              src={block.src}
              alt={block.alt}
              className="w-full aspect-[16/9] object-cover"
            />
            {block.caption && (
              <figcaption className="mt-3 text-sm text-[#666] italic">
                {block.caption}
              </figcaption>
            )}
          </figure>
        );
      
      case 'list':
        return (
          <ul key={index} className="my-6 space-y-3">
            {block.items.map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-[#1A5C6E]">
                <span className="w-1.5 h-1.5 bg-[#2CA8A0] rounded-full mt-2 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        );
      
      case 'cta':
        return (
          <div key={index} className="my-12 p-8 bg-[#F9F6EF] border border-[#FFFFFF]">
            <h3 className="text-xl font-light text-white mb-2">{block.title}</h3>
            <p className="text-[#888] mb-6">{block.description}</p>
            <Link
              to={block.buttonLink}
              className="inline-flex items-center gap-2 text-sm font-medium text-black bg-[#2CA8A0] px-6 py-3 hover:bg-[#2CA8A0] transition-colors"
            >
              {block.buttonText}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F6EF]">
      <SEO
        title={insight.title}
        description={insight.excerpt || insight.title}
        path={`/insights/${insight.slug}`}
      />
      <Header />
      <section className="pt-32 pb-12">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <Link 
            to="/insights"
            className="inline-flex items-center gap-2 text-sm text-[#888] hover:text-white transition mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('insights.backToInsights')}
          </Link>

          <p className="text-[11px] font-medium text-[#2CA8A0] uppercase tracking-[0.15em] mb-4">
            {categoryTranslations[category?.name] || category?.name}
          </p>
          
          <h1 className="text-3xl lg:text-4xl font-light text-white leading-tight mb-6">
            {insight.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-sm text-[#666]">
            <span className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {insight.author}
            </span>
            <span className="w-1 h-1 bg-[#444] rounded-full" />
            <span>{formatDate(insight.publishedAt)}</span>
            <span className="w-1 h-1 bg-[#444] rounded-full" />
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {insight.readTime}
            </span>
          </div>
        </div>
      </section>

      {/* Hero Image */}
      <section className="pb-12">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <img 
            src={insight.heroImage}
            alt={insight.title}
            className="w-full aspect-[21/9] object-cover"
          />
        </div>
      </section>

      {/* Article Content */}
      <article className="pb-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          {insight.content.map((block, index) => renderContentBlock(block, index))}
        </div>
      </article>

      {/* Author Box */}
      <section className="py-12 border-t border-[#FFFFFF]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="flex items-start gap-6">
            <div className="w-16 h-16 bg-[#FFFFFF] rounded-full flex items-center justify-center shrink-0">
              <User className="w-8 h-8 text-[#555]" />
            </div>
            <div>
              <p className="text-[11px] text-[#555] uppercase tracking-wider mb-1">
                {language === 'es' ? 'Escrito por' : 'Written by'}
              </p>
              <p className="text-lg font-medium text-white mb-2">{insight.author}</p>
              <p className="text-sm text-[#888]">
                {language === 'es' 
                  ? 'Mundalo asesora a familias y profesionales en su mudanza a México - visas, vivienda, banca, salud, y todo lo necesario para vivir con libertad.'
                  : 'Mundalo guides families and professionals through their move to Mexico - visas, housing, banking, healthcare, and everything you need to live freely.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Articles */}
      {relatedInsights.length > 0 && (
        <section className="py-16 bg-[#F9F6EF] border-t border-[#FFFFFF]">
          <div className="max-w-5xl mx-auto px-6 lg:px-12">
            <h2 className="text-xl font-light text-white mb-10">{t('insights.relatedArticles')}</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {relatedInsights.map((related) => (
                <Link
                  key={related.id}
                  to={`/insights/${related.slug}`}
                  className="group"
                >
                  <div className="aspect-[16/10] overflow-hidden bg-[#FFFFFF] mb-4">
                    <img 
                      src={related.heroImage}
                      alt={related.title}
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                    />
                  </div>
                  <p className="text-[11px] font-medium text-[#555] uppercase tracking-wider mb-2">
                    {categoryTranslations[category?.name] || category?.name}
                  </p>
                  <h3 className="text-lg font-light text-white group-hover:text-[#ccc] transition">
                    {related.title}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* More Insights CTA */}
      <section className="py-12 border-t border-[#FFFFFF]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <Link 
            to="/insights"
            className="inline-flex items-center gap-2 text-sm text-[#888] hover:text-white transition"
          >
            {language === 'es' ? 'Ver Todas las Perspectivas' : 'View All Insights'}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InsightArticle;
